import React, { useEffect, useState } from 'react';
import {
  ThemeProvider,
  createTheme,
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  Snackbar,
  IconButton,
  useMediaQuery,
  InputAdornment
} from '@mui/material';
import {
  ContentCopy,
  Preview,
  Code,
  IntegrationInstructions
} from '@mui/icons-material';

import { styled, useTheme } from "@mui/material/styles";
import { toast, ToastContainer } from 'react-toastify';
import { GetCompanySettings } from '../logic/CompanySettingsLogic';

const EmbeddableForm = () => {
    
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const url = window.location.origin+'/request/create/';  
  const [iframeUrl, setIframeUrl] = useState('');
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const iframeCode = `<iframe width="100%" height="855px" style="border: none;" src="${iframeUrl}"></iframe>`;
  
  useEffect(() => {
    const fetchToken = async() =>{
        const company = await GetCompanySettings(); 
        if(company?.result && company?.result.token) {
          setIframeUrl(`${url}${company.result.token}`);
        }
    }        
    fetchToken();
  },[])

  const handleCopyCode = () => {
    navigator.clipboard.writeText(iframeCode);
    toast.info('Embed code copied to clipboard!', {
      position: "top-right",
      autoClose: 3000,
        theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
    });
  }

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(iframeUrl);
    toast.info('Request Form URL copied to clipboard!', {
      position: "top-right",
      autoClose: 3000,
        theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
    });
  };

  return (    
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <IntegrationInstructions sx={{ fontSize: 48, color: "#23A26D", mb: 2 }} />
            <Typography variant="h3" gutterBottom>
              Embeddable Service Request Form
            </Typography>
            <Typography variant="h6" color="text.secondary">
                Copy and paste the code below to embed this form directly into your website.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {/* Preview Section */}
            <Grid item xs={12} md={6}>
              <Card elevation={3}>
                <CardContent sx={{ p: 0 }}>
                <Box sx={{bgcolor: "#23A26D", color:"white" , p: 2, display: 'flex', alignItems: 'center'}}>
                    <Preview sx={{ mr: 1 }} />
                    <Typography variant="h6" color={'white'}>Form Preview</Typography>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      This is how your form will appear when embedded on a website:
                    </Typography>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        height: 300,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#f9f9f9'
                      }}
                    >
                    <iframe
                        src={iframeUrl}
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                        title="Form Preview"
                      />                      
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Embed Code Section */}
            <Grid item xs={12} md={6}>
              <Card elevation={3}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{bgcolor: "#23A26D", color:"white", p: 2, display: 'flex', alignItems: 'center'}}>
                    <Code sx={{ mr: 1 }} />
                    <Typography variant="h6" color="white">Embed Code</Typography>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Copy and paste this code into your website's HTML:
                    </Typography>
                    
                    <TextField
                      fullWidth
                      label="Form URL"
                      value={iframeUrl}
                      onChange={(e) => setIframeUrl(e.target.value)}
                      margin="normal"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="copy url"
                              onClick={handleCopyUrl}
                              edge="end"
                              color="#23A26D"
                            >
                              <ContentCopy />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        mt: 2, 
                        bgcolor: theme.palette.mode === 'dark' ? '#1B2431' : '#f5f5f5', 
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem'
                      }}
                    >
                      {iframeCode}
                    </Paper>
                    
                    <Button
                      fullWidth                      
                      color="main"
                      variant="contained"
                      startIcon={<ContentCopy />}
                      onClick={handleCopyCode}
                      sx={{ mt: 2 }}
                    >
                      Copy Embed Code
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Instructions Section */}
          <Box mt={4}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <IntegrationInstructions sx={{ mr: 1, color: 'primary.main' }} />
                Implementation Instructions
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>1. Copy the Code</Typography>
                  <Typography variant="body2" paragraph>
                    Click the "Copy Embed Code" button to copy the iframe code to your clipboard.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>2. Paste in Your HTML</Typography>
                  <Typography variant="body2" paragraph>
                    Paste the code anywhere in the &lt;body&gt; section of your website's HTML where you want the form to appear.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>3. Adjust Styling (Optional)</Typography>
                  <Typography variant="body2" paragraph>
                    You can adjust the width and height attributes to better fit your website's layout.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>4. Test the Form</Typography>
                  <Typography variant="body2" paragraph>
                    Always test the form on your website to ensure it's working correctly and looks good on all devices.
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Box>         
          {/*<ToastContainer />*/}
        </Container>
      </Box>    
  );
};

export default EmbeddableForm;